"use client"

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
  SidebarRail,
} from '@/components/ui/sidebar'
import { Home, Folder, Settings, User, Users, HelpCircle, Activity, Circle, Square, Diamond, PanelLeft } from 'lucide-react'
import { useMe } from '@/hooks/use-me'
import { useClientSelection } from '@/contexts/client-selection'
import { clientHomeRoute } from '@/utils/clientRoutes'


const NAV_ITEMS = [
  { href: '/admin', label: 'Dashboard', icon: Home, access: 'all' as const },
  { href: '/admin/pentest', label: 'Pentest', icon: Folder, access: 'all' as const },
]

export default function AppSidebar({ variant = 'desktop', onNavigateAction }: { variant?: 'desktop' | 'mobile'; onNavigateAction?: () => void }) {
  const pathname = usePathname()
  const appPathname = React.useMemo(() => {
    if (!pathname?.startsWith('/admin')) return pathname
    const withoutAdmin = pathname.slice('/admin'.length)
    return withoutAdmin || '/'
  }, [pathname])
  const { data: me } = useMe()
  const isClient = !!me && me.user_type === 'client' && !me.is_staff && !me.is_superuser
  const staffRole = me?.staff_role ?? (me?.is_superuser ? 'admin' : undefined)
  const { selectedClient, openSelector } = useClientSelection()
  const [hydrated, setHydrated] = React.useState(false)
  React.useEffect(() => setHydrated(true), [])

  const moduleFlags = (me as any)?.module_access as Record<string, boolean> | undefined
  const hasModuleFlags = !!moduleFlags && Object.keys(moduleFlags).length > 0
  const hasModule = React.useCallback((key: string) => {
    if (!hasModuleFlags) return true
    return moduleFlags?.[key] === true
  }, [hasModuleFlags, moduleFlags])

  const section: 'dashboard' | 'pentest' | 'vmaas' | 'sales' = React.useMemo(() => {
    if (isClient) return 'dashboard'
    if (!appPathname || appPathname === '/') return 'dashboard'
    if (appPathname.startsWith('/pentest') && hasModule('pentest')) return 'pentest'
    // Keep Pentest sidebar on related routes
    if (appPathname.startsWith('/projects') && hasModule('projects')) return 'pentest'
    if (appPathname.startsWith('/scoping') && hasModule('pentest')) return 'pentest'
    if (appPathname.startsWith('/healthcheck') && hasModule('healthcheck')) return 'pentest'
    if (appPathname.startsWith('/sales') && hasModule('sales')) return 'pentest'
    if (appPathname.startsWith('/vmaas')) return 'vmaas'
    // Neutral routes: try to keep last active section
    if (appPathname.startsWith('/profile') || appPathname.startsWith('/settings') || appPathname.startsWith('/help')) {
      // Only read localStorage after client hydration to avoid SSR/client mismatch
      if (hydrated && typeof window !== 'undefined') {
        const last = window.localStorage.getItem('active.section')
        if (last === 'pentest' || last === 'vmaas' || last === 'sales') {
          return last as any
        }
      }
    }
    return 'dashboard'
  }, [appPathname, hydrated, isClient, hasModule])

  const isPentestPortal = !isClient && section === 'pentest'
  const clientWorkspaceName = selectedClient?.name || me?.company || 'Workspace'
  const clientWorkspaceHref = clientHomeRoute(!!me?.has_asm_portfolios)
  const isClientWorkspaceActive = pathname?.startsWith('/healthcheck') || pathname?.startsWith('/portfolio/healthcheck')
  const portalTitle = isClient ? 'Command centre' : isPentestPortal ? 'Pentest command' : 'Pentest Portal'
  const portalEyebrow = isClient ? 'COGNISYS ASM' : 'COGNISYS OPS'
  const sidebarShellClass = [
    variant === 'desktop' ? '!h-dvh !min-h-dvh' : '!h-dvh !min-h-dvh w-[80vw] max-w-[320px] shadow-lg',
    '!top-0 !bg-[#0F172A] text-slate-200 border-[#253247]',
  ].filter(Boolean).join(' ')
  const navButtonClass = 'h-10 gap-3 rounded-md px-3 text-sm leading-5 transition-colors hover:bg-slate-800/80 data-[state=open]:bg-slate-800 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0'
  const activeNavButtonClass = `${navButtonClass} !bg-[#2563EB] font-bold !text-white hover:!bg-[#2563EB] [&_svg]:!text-white`
  const inactiveNavButtonClass = `${navButtonClass} font-medium !text-slate-300 hover:!bg-slate-800/80 hover:!text-white [&_svg]:!text-slate-500`
  const isPentestHome = appPathname === '/pentest' || appPathname === '/pentest/'

  return (
    <Sidebar collapsible="icon" className={sidebarShellClass} style={{ backgroundColor: '#0F172A' }} data-testid="app-sidebar">
      <SidebarHeader className="border-[#253247] !bg-[#0F172A] px-[22px] pb-5 pt-7">
        <div className="space-y-1.5 group-data-[collapsed]/sidebar:sr-only">
          <div className="text-[12px] font-extrabold uppercase leading-4 tracking-[0.1em] text-[#93C5FD]">{portalEyebrow}</div>
          <div className="text-xl font-extrabold leading-[26px] text-slate-50">{portalTitle}</div>
        </div>
        <div className="hidden h-8 w-8 items-center justify-center rounded-md border border-[#253247] text-[#93C5FD] group-data-[collapsed]/sidebar:flex">
          <PanelLeft className="h-4 w-4" />
        </div>
      </SidebarHeader>
      <SidebarContent className="bg-[#0F172A]">
        <SidebarGroup className="py-0">
          <SidebarGroupLabel className="px-6 pb-1 pt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
            {isClient ? 'Workspace' : ['pentest','vmaas','sales'].includes(section) ? 'Dashboard' : 'Navigation'}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            {isClient ? (
              <SidebarMenu className="space-y-1.5 px-3 py-2">
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={clientWorkspaceName} isActive={isClientWorkspaceActive} className={isClientWorkspaceActive ? activeNavButtonClass : inactiveNavButtonClass}>
                    <Link href={clientWorkspaceHref} onClick={onNavigateAction}>
                      <Activity className="w-5 h-5" />
                      <span>{clientWorkspaceName}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            ) : section === 'pentest' ? (
              <>
                <SidebarMenu className="space-y-1.5 px-3 py-2">
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Dashboard" isActive={isPentestHome} className={isPentestHome ? activeNavButtonClass : inactiveNavButtonClass}>
                      <Link href="/admin/pentest" onClick={onNavigateAction}>
                        <Square className="w-5 h-5" />
                        <span>Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Action Centre" isActive={appPathname?.startsWith('/pentest/action-centre')} className={appPathname?.startsWith('/pentest/action-centre') ? activeNavButtonClass : inactiveNavButtonClass}>
                      <Link href="/admin/pentest/action-centre" onClick={onNavigateAction}>
                        <Circle className="w-5 h-5" />
                        <span>Action Centre</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Projects" isActive={appPathname?.startsWith('/projects')} className={appPathname?.startsWith('/projects') ? activeNavButtonClass : inactiveNavButtonClass}>
                      <Link href="/admin/projects" onClick={onNavigateAction}>
                        <Folder className="w-5 h-5" />
                        <span>Projects</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Scoping" isActive={appPathname?.startsWith('/scoping')} className={appPathname?.startsWith('/scoping') ? activeNavButtonClass : inactiveNavButtonClass}>
                      <Link href="/admin/scoping" onClick={onNavigateAction}>
                        <Diamond className="w-5 h-5" />
                        <span>Scoping</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </>
            ) : ['vmaas','sales'].includes(section) ? (
              <SidebarMenu className="space-y-1.5 px-3 py-2">
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Dashboard" isActive={true} className={activeNavButtonClass}>
                    <Link href={`/admin/${section}`} onClick={onNavigateAction}>
                      <Home className="w-5 h-5" />
                      <span>Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            ) : (
              <>
                <SidebarMenu className="space-y-1.5 px-3 py-2">
                  {NAV_ITEMS.filter(({ access, href }) => {
                    if (isClient && href === '/admin/pentest') return false
                    if (href === '/admin/pentest' && !hasModule('pentest')) return false
                    if (href === '/admin/sales' && !hasModule('sales')) return false
                    if (access === 'all') return true
                    if (access === 'internalFull') {
                      if (!me?.is_staff || isClient) return false
                      return staffRole !== 'sales_team'
                    }
                    return true
                  }).map(({ href, label, icon: Icon }) => {
                    const active = pathname === href || (href !== '/admin' && pathname?.startsWith(href))
                    return (
                      <SidebarMenuItem key={href}>
                        <SidebarMenuButton asChild tooltip={label} isActive={active} className={active ? activeNavButtonClass : inactiveNavButtonClass}>
                          <Link href={href} onClick={onNavigateAction}>
                            <Icon className="w-5 h-5" />
                            <span>{label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>

              </>
            )}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="space-y-3 border-[#253247] bg-[#0F172A] px-3 py-4">
        {!isClient && (
          <SidebarMenu className="space-y-1.5 px-0">
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Clients" className={inactiveNavButtonClass} onClick={() => { openSelector(); onNavigateAction?.() }}>
                <Users className="w-5 h-5" />
                <span>Clients</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Profile" className={inactiveNavButtonClass}>
                <Link href="/profile" onClick={onNavigateAction}>
                  <User className="w-5 h-5" />
                  <span>Profile</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Settings" className={inactiveNavButtonClass}>
                <Link href="/admin/settings" onClick={onNavigateAction}>
                  <Settings className="w-5 h-5" />
                  <span>Settings</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Help" className={inactiveNavButtonClass}>
                <Link href="/help" onClick={onNavigateAction} data-testid="help-link">
                  <HelpCircle className="w-5 h-5" />
                  <span>Help</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        {isClient ? null : (
          <div className="border-t border-[#253247] px-1 pt-4 group-data-[collapsed]/sidebar:hidden">
            <div className="text-xs leading-4 text-slate-400">Operations owner</div>
            <div className="mt-2 text-sm font-extrabold leading-[18px] text-slate-50">Pentest Delivery</div>
            <div className="mt-2 text-xs leading-4 text-slate-300">Active delivery desk</div>
          </div>
        )}
        {isClient ? (
          <div className="border-t border-[#253247] px-1 pt-4 group-data-[collapsed]/sidebar:hidden">
            <div className="text-xs leading-4 text-slate-400">Active organisation</div>
            <div className="mt-2 text-sm font-extrabold leading-[18px] text-slate-50">{clientWorkspaceName}</div>
            <div className="mt-2 text-xs leading-4 text-slate-300">Security overview</div>
          </div>
        ) : null}
      </SidebarFooter>
      {/* Optional version text at the very bottom can be re-added here if desired */}
      <SidebarRail className="border-[#253247] bg-[#0F172A] text-slate-300 shadow-none hover:text-white" />
    </Sidebar>
  )
}
